import type { CourseAttributes, TopicAttributes } from '@/server/models'

import {
  COURSE_SLUG_SAT_BOOTCAMP_MATH,
  COURSE_SLUG_SAT_BOOTCAMP_READING,
  LIVE_HELP_TOPIC,
  OTHER_DOMAIN,
  PILOT_DOMAIN,
  SERIES_COURSE_DOMAIN,
} from '@/queries/constants'
import { SQL, type SQLFragment } from '@/client/query'
import { SAT_BOOTCAMP_WORLD_SLUG } from '@/client/services/worlds/constants'

import {
  type AssociationDefinitions,
  type GroupBy,
  type IncludeAssociations,
  includeAssociationsToSql,
  makeQueryModelV2,
  type QueryOrder,
  queryGroupByToSQL,
  queryOrderToSQL,
} from './common'
import TopicQ from './TopicQ'
import WorldQ from './WorldQ'

const CourseQ = {
  ...makeQueryModelV2<CourseAttributes>('course')([
    'id',
    'name',
    'domain',
    'icon',
    'weight',
    'created_at',
    'deleted_at',
    'updated_at',
    'ka_url',
    'slug',
  ]),
  associations: (): AssociationDefinitions => ({
    Topics: { query: TopicQ, foreignKey: TopicQ.course_id, sourceKey: CourseQ.id },
    Worlds: { query: WorldQ, foreignKey: WorldQ.course_id, sourceKey: CourseQ.id },
  }),
  where: (
    {
      id,
      name,
      domain,
      slug,
      includeSeries = domain === SERIES_COURSE_DOMAIN,
      include = [],
      groupBy = null,
      order = null,
      paranoid = true,
    }: {
      id?: number | null | SQLFragment<number>
      name?: string | null | SQLFragment<string>
      slug?: string | null | SQLFragment<string>
      domain?: string | null | SQLFragment<string>
      includeSeries?: boolean
      include?: IncludeAssociations
      groupBy?: GroupBy | null
      order?: QueryOrder | null
      paranoid?: boolean
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<CourseAttributes> => SQL`
        FROM ${CourseQ}
        ${includeAssociationsToSql(CourseQ, include)}
        WHERE
            ${id != null ? SQL`${CourseQ.id} = ${id} AND` : SQL``}
            ${name != null ? SQL`${CourseQ.name} = ${name} AND` : SQL``}
            ${slug != null ? SQL`${CourseQ.slug} = ${slug} AND` : SQL``}
            ${domain != null ? SQL`${CourseQ.domain} = ${domain} AND` : SQL``}
            ${includeSeries ? SQL`` : SQL`${CourseQ.domain} != ${SERIES_COURSE_DOMAIN} AND`}
            ${paranoid ? SQL`${CourseQ.deleted_at} IS NULL AND` : SQL``}
            ${customWhere != null ? customWhere : SQL`TRUE`}
        ${queryGroupByToSQL(groupBy)}
        ${queryOrderToSQL(order)}
    `,

  getAll: ({
    excludeOther = false,
    includePilot = false,
    includeSATBootcamp = false,
    customWhere,
  }: {
    excludeOther?: boolean
    includeHidden?: boolean
    includePilot?: boolean
    includeSATBootcamp?: boolean
    customWhere?: SQLFragment<boolean>
  }): SQLFragment<CourseAttributes> => SQL`
        ${CourseQ.where(
          {},
          SQL`
          (${excludeOther === true ? SQL`${CourseQ.domain} != ${OTHER_DOMAIN}` : SQL`TRUE`}) AND
          (${includePilot === true ? SQL`TRUE` : SQL`${CourseQ.domain} != ${PILOT_DOMAIN}`}) AND
          (${includeSATBootcamp === true ? SQL`TRUE` : SQL`NOT (${CourseQ.isSATBootcampCourse()})`}) AND
          ${customWhere != null ? customWhere : SQL`TRUE`}
         `
        )}
        ORDER BY ${CourseQ.weight} DESC
    `,
  getByName: (name: string | null): SQLFragment<CourseAttributes> => SQL`
        ${CourseQ.where({ name })} LIMIT 1
    `,
  isSATBootcampCourse: (): SQLFragment<boolean> => SQL`
   (${CourseQ.slug} = ${COURSE_SLUG_SAT_BOOTCAMP_MATH} OR ${CourseQ.slug} = ${COURSE_SLUG_SAT_BOOTCAMP_READING})`,

  getDefault: (): SQLFragment<CourseAttributes> => SQL`
        ${CourseQ.getAll({})} LIMIT 1
    `,
  // Filters out Live Help topics by default.
  getTopics: ({
    excludeQa = false,
    excludeChallenge = false,
    excludeOther = false,
    includeInactive = false,
  } = {}): SQLFragment<TopicAttributes> =>
    TopicQ.where(
      {
        courseId: CourseQ.id,
        excludeQa,
        excludeChallenge,
        excludeOther,
        includeInactive,
        order: [[TopicQ.weight, 'desc']],
      },
      SQL`${TopicQ.name} != ${LIVE_HELP_TOPIC}`
    ),

  getCertificationTopics: (): SQLFragment<TopicAttributes> =>
    TopicQ.where({ courseId: CourseQ.id }, TopicQ.requiresCertification()),

  matchesWorldSlug: (worldSlug: string): SQLFragment<boolean> =>
    SQL`(${
      worldSlug === SAT_BOOTCAMP_WORLD_SLUG
        ? CourseQ.isSATBootcampCourse()
        : SQL`EXISTS(SELECT 1 ${WorldQ.where({ slug: worldSlug, courseId: CourseQ.id })})`
    }
    OR ${CourseQ.domain} = ${SERIES_COURSE_DOMAIN})`,

  input__id: SQL<number>`"course__input_id"`,
  input__label: SQL<string>`"course__input_label"`,
  input__slug: SQL<string>`"course__input__slug"`,
  input__domain: SQL<string>`"course__input_domain"`,
  input__options: SQL<{ label: string; value: string; icon: string }[]>`"course__input_options"`,
  getCoursesAndTopicsSelectInputs: ({
    userId = null,
    includeCourseChallenges = false,
    includeLiveHelp = false,
    allowInactive = false,
    includePilot = false,
    worldSlug = null,
  }: {
    userId?: number | null
    includeCourseChallenges?: boolean
    includeLiveHelp?: boolean
    allowInactive?: boolean
    includeHidden?: boolean
    includePilot?: boolean
    worldSlug?: string | null
  }): SQLFragment<{
    id: number
    label: string
    domain: string
    options: { label: string; value: string }[]
  }> => SQL`FROM (SELECT
            ${CourseQ.id} as ${CourseQ.input__id},
            ${CourseQ.name} as ${CourseQ.input__label},
            ${CourseQ.slug} as ${CourseQ.input__slug},
            ${CourseQ.domain} as ${CourseQ.input__domain},
            json_agg(
                json_build_object(
                    'value', ${TopicQ.id}::text,
                    'label', ${TopicQ.name},
                    'icon', ${TopicQ.icon})
                ORDER BY ${TopicQ.weight} DESC
            ) as ${CourseQ.input__options}
        FROM ${TopicQ}
        INNER JOIN ${CourseQ} ON ${CourseQ.id} = ${TopicQ.course_id}
        WHERE ${TopicQ.deleted_at} IS NULL
        ${worldSlug == null ? SQL`` : SQL`AND ${CourseQ.matchesWorldSlug(worldSlug)}`}
        ${includeCourseChallenges ? SQL`` : SQL`AND ${TopicQ.type} != 'challenge'`}
        ${includeLiveHelp ? SQL`` : SQL`AND ${TopicQ.name} != ${LIVE_HELP_TOPIC}`}
        ${includePilot ? SQL`` : SQL`AND ${CourseQ.domain} != ${PILOT_DOMAIN}`}
        AND ${CourseQ.deleted_at} IS NULL
        ${
          userId == null
            ? !allowInactive
              ? SQL`AND ${TopicQ.isActive()}`
              : SQL``
            : SQL`AND (${TopicQ.canUserTutor(userId, allowInactive)})`
        }
        GROUP BY ${CourseQ.name}, ${CourseQ.id}, ${CourseQ.weight}
        ORDER BY ${CourseQ.weight} DESC) AS courses`,
}

export default CourseQ
